const initSiteHeader = () => {
  const siteHeader = document.querySelector('[data-site-header]');

  if (!siteHeader) return;
  
  const desktopHeader = window.matchMedia('(min-width: 1024px)');
  const stickThreshold = 32;
  const releaseThreshold = 4;
  let isStuck = false;
  let animationFrame = null;

  const updateHeaderState = () => {
    animationFrame = null;

    const scrollPosition = Math.max(0, window.scrollY);
    const nextIsStuck = desktopHeader.matches
      ? (isStuck ? scrollPosition > releaseThreshold : scrollPosition >= stickThreshold)
      : false;

    if (nextIsStuck === isStuck) return;

    isStuck = nextIsStuck;
    siteHeader.classList.toggle('is-stuck', isStuck);
  };

  const scheduleHeaderUpdate = () => {
    if (animationFrame !== null) return;

    animationFrame = window.requestAnimationFrame(updateHeaderState);
  };

  updateHeaderState();
  window.addEventListener('scroll', scheduleHeaderUpdate, { passive: true });
  window.addEventListener('resize', scheduleHeaderUpdate);
};

const initDesktopNavigation = () => {
  const navigation = document.querySelector('[data-desktop-navigation]');
  if (!navigation) return;

  const closeMenus = () => {
    navigation.classList.add('is-desktop-menu-closing');

    if (document.activeElement instanceof HTMLElement && navigation.contains(document.activeElement)) {
      document.activeElement.blur();
    }
  };

  const allowMenus = () => {
    navigation.classList.remove('is-desktop-menu-closing');
  };

  const blurStaleFocus = (container) => {
    const active = document.activeElement;

    if (active instanceof HTMLElement && navigation.contains(active) && !container.contains(active)) {
      active.blur();
    }
  };

  navigation.querySelectorAll('[data-desktop-top-menu-item]').forEach((item) => {
    item.addEventListener('pointerenter', () => {
      allowMenus();
      blurStaleFocus(item);
    });
  });

  navigation.querySelectorAll('.header-menu-item').forEach((item) => {
    item.addEventListener('pointerenter', () => blurStaleFocus(item));
  });

  navigation.addEventListener('mouseleave', allowMenus);

  navigation.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest('a');

    if (!link || !navigation.contains(link) || !link.closest('[data-desktop-submenu]')) return;

    if (link.getAttribute('href') === '#') {
      event.preventDefault();
    }

    closeMenus();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeMenus();
    }
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSiteHeader, { once: true });
  document.addEventListener('DOMContentLoaded', initDesktopNavigation, { once: true });
} else {
  initSiteHeader();
  initDesktopNavigation();
}

const readingProgress = document.querySelector('[data-reading-progress]');
const articleBody = document.querySelector('[data-article-content]');

if (readingProgress && articleBody) {
  const updateReadingProgress = () => {
    const articleTop = articleBody.getBoundingClientRect().top + window.scrollY;
    const articleHeight = articleBody.offsetHeight;
    const scrollable = Math.max(1, articleHeight - window.innerHeight);
    const progress = Math.min(1, Math.max(0, (window.scrollY - articleTop) / scrollable));

    readingProgress.style.width = `${Math.round(progress * 100)}%`;
  };

  updateReadingProgress();
  window.addEventListener('scroll', updateReadingProgress, { passive: true });
  window.addEventListener('resize', updateReadingProgress);
}

const tocLinks = [...document.querySelectorAll('[data-toc-link]')];

if (tocLinks.length > 0) {
  const headings = tocLinks
    .map((link) => {
      const id = decodeURIComponent(link.hash.slice(1));
      return document.getElementById(id);
    })
    .filter(Boolean);

  const setActiveTocLink = (headingId) => {
    tocLinks.forEach((link) => {
      const isActive = decodeURIComponent(link.hash.slice(1)) === headingId;
      link.classList.toggle('is-active', isActive);

      if (isActive) {
        link.setAttribute('aria-current', 'location');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  };

  const updateActiveTocLink = () => {
    const activeHeading = headings.reduce((active, heading) => {
      return heading.getBoundingClientRect().top <= 140 ? heading : active;
    }, headings[0]);

    if (activeHeading) setActiveTocLink(activeHeading.id);
  };

  updateActiveTocLink();
  window.addEventListener('scroll', updateActiveTocLink, { passive: true });
  window.addEventListener('resize', updateActiveTocLink);
}

document.querySelectorAll('[data-copy-link]').forEach((button) => {
  button.addEventListener('click', async () => {
    const link = button.getAttribute('data-copy-link');
    if (!link) return;

    try {
      await navigator.clipboard.writeText(link);
      const originalText = button.textContent;
      button.textContent = 'Copied';
      button.classList.add('is-copied');

      window.setTimeout(() => {
        button.textContent = originalText;
        button.classList.remove('is-copied');
      }, 1800);
    } catch (error) {
      window.location.href = link;
    }
  });
});

document.querySelectorAll('[data-problems-track]').forEach((track) => {
  const prev = document.querySelector('[data-problems-prev]');
  const next = document.querySelector('[data-problems-next]');
  if (!prev || !next) return;

  const step = () => {
    const card = track.querySelector('article');
    if (!card) return 320;
    const styles = getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap || '24') || 24;
    return card.getBoundingClientRect().width + gap;
  };

  prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
  next.addEventListener('click', () => track.scrollBy({ left:  step(), behavior: 'smooth' }));
});

document.querySelectorAll('[data-mobile-carousel]').forEach((track) => {
  const slides = [...track.querySelectorAll('[data-carousel-slide]')];
  const dotsContainer = track.nextElementSibling?.matches('[data-carousel-dots]') ? track.nextElementSibling : null;
  const dots = [...(dotsContainer?.querySelectorAll('[data-carousel-dot]') ?? [])];
  if (slides.length === 0 || dots.length === 0) return;

  let animationFrame = null;

  const setActive = (activeIndex) => {
    dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === activeIndex)));
  };

  const updateActive = () => {
    animationFrame = null;

    const trackRect = track.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;
    let activeIndex = 0;
    let closest = Infinity;

    slides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - trackCenter);

      if (distance < closest) {
        closest = distance;
        activeIndex = index;
      }
    });

    setActive(activeIndex);
  };

  track.addEventListener('scroll', () => {
    if (animationFrame !== null) return;

    animationFrame = window.requestAnimationFrame(updateActive);
  }, { passive: true });

  dots.forEach((dot, index) => {
    dot.addEventListener('click', () => {
      const slide = slides[index];
      if (!slide) return;

      const trackRect = track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      track.scrollTo({
        left: track.scrollLeft + slideRect.left - trackRect.left - (trackRect.width - slideRect.width) / 2,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    });
  });
});

document.querySelectorAll('[data-industries-switcher]').forEach((switcher) => {
  const triggers = [...switcher.querySelectorAll('[data-industry-trigger]')];
  const panels = [...switcher.querySelectorAll('[data-industry-panel]')];

  if (triggers.length === 0 || panels.length === 0) return;

  const setIndustry = (index) => {
    triggers.forEach((trigger) => {
      const isActive = trigger.dataset.industryTrigger === index;
      trigger.setAttribute('aria-selected', String(isActive));
      trigger.closest('li')?.classList.toggle('active', isActive);
    });

    panels.forEach((panel) => {
      const isActive = panel.dataset.industryPanel === index;
      panel.hidden = !isActive;
      panel.classList.toggle('active', isActive);
    });
  };

  setIndustry('0');

  triggers.forEach((trigger) => {
    const index = trigger.dataset.industryTrigger;
    if (!index) return;

    trigger.addEventListener('mouseenter', () => setIndustry(index));
    trigger.addEventListener('focus', () => setIndustry(index));
    trigger.addEventListener('click', () => setIndustry(index));
  });
});

document.querySelectorAll('[data-faq-list]').forEach((faqList) => {
  const items = [...faqList.querySelectorAll('details')];
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const animationFrames = new WeakMap();

  const cancelAnimationFrameForItem = (item) => {
    const frame = animationFrames.get(item);

    if (frame) {
      window.cancelAnimationFrame(frame);
      animationFrames.delete(item);
    }
  };

  const clearAnswerStyles = (answer) => {
    answer.style.height = '';
  };

  const finishClose = (item, answer) => {
    item.open = false;
    item.classList.remove('is-open', 'is-closing');
    clearAnswerStyles(answer);
  };

  const closeItem = (item) => {
    if (!item.open) return;

    const summary = item.querySelector('summary');
    const answer = item.querySelector('[data-faq-answer]');
    if (!answer) {
      item.open = false;
      return;
    }

    summary?.setAttribute('aria-expanded', 'false');
    cancelAnimationFrameForItem(item);

    if (motionQuery.matches) {
      finishClose(item, answer);
      return;
    }

    answer.style.height = `${answer.getBoundingClientRect().height || answer.scrollHeight}px`;
    item.classList.remove('is-open');
    item.classList.add('is-closing');

    const frame = window.requestAnimationFrame(() => {
      answer.style.height = '0px';

      animationFrames.delete(item);
    });
    animationFrames.set(item, frame);
  };

  const openItem = (item) => {
    const summary = item.querySelector('summary');
    const answer = item.querySelector('[data-faq-answer]');
    if (!answer) return;
    const wasClosing = item.classList.contains('is-closing');
    const startHeight = wasClosing ? answer.getBoundingClientRect().height : 0;

    items.forEach((otherItem) => {
      if (otherItem !== item) closeItem(otherItem);
    });

    cancelAnimationFrameForItem(item);

    item.open = true;
    answer.style.height = `${startHeight}px`;
    item.classList.remove('is-closing');
    item.classList.add('is-open');
    summary?.setAttribute('aria-expanded', 'true');

    if (motionQuery.matches) {
      clearAnswerStyles(answer);
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      answer.style.height = `${answer.scrollHeight}px`;

      animationFrames.delete(item);
    });
    animationFrames.set(item, frame);
  };

  items.forEach((item, index) => {
    const summary = item.querySelector('summary');
    const answer = item.querySelector('[data-faq-answer]');
    if (!summary || !answer) return;

    summary.setAttribute('aria-expanded', String(item.open));
    item.classList.toggle('is-open', item.open);

    if (item.open && items.findIndex((otherItem) => otherItem.open) !== index) {
      item.open = false;
      item.classList.remove('is-open');
      summary.setAttribute('aria-expanded', 'false');
    }

    summary.addEventListener('click', (event) => {
      event.preventDefault();

      if (item.open && !item.classList.contains('is-closing')) {
        closeItem(item);
        return;
      }

      openItem(item);
    });

    answer.addEventListener('transitionend', (event) => {
      if (event.target !== answer || event.propertyName !== 'height') return;

      if (item.classList.contains('is-closing')) {
        finishClose(item, answer);
        return;
      }

      if (item.open) {
        clearAnswerStyles(answer);
      }
    });
  });
});

document.querySelectorAll('[data-mobile-menu-toggle]').forEach((toggle) => {
  const menuId = toggle.getAttribute('aria-controls');
  const menu = menuId ? document.getElementById(menuId) : null;
  const closeButtons = menu ? menu.querySelectorAll('[data-mobile-menu-close]') : [];
  const lines = {
    top: toggle.querySelector('[data-mobile-menu-line="top"]'),
    middle: toggle.querySelector('[data-mobile-menu-line="middle"]'),
    bottom: toggle.querySelector('[data-mobile-menu-line="bottom"]'),
  };

  if (!menu) return;

  const setOpen = (isOpen) => {
    toggle.setAttribute('aria-expanded', String(isOpen));
    toggle.setAttribute(
      'aria-label',
      isOpen ? (toggle.dataset.closeLabel || 'Close main menu') : (toggle.dataset.openLabel || 'Open main menu')
    );
    menu.classList.toggle('-translate-x-full', !isOpen);
    menu.classList.toggle('translate-x-0', isOpen);
    menu.classList.toggle('opacity-0', !isOpen);
    menu.classList.toggle('opacity-100', isOpen);
    menu.classList.toggle('pointer-events-none', !isOpen);
    document.documentElement.classList.toggle('overflow-hidden', isOpen);
    lines.top?.classList.toggle('translate-y-[7px]', isOpen);
    lines.top?.classList.toggle('rotate-45', isOpen);
    lines.middle?.classList.toggle('opacity-0', isOpen);
    lines.bottom?.classList.toggle('-translate-y-[7px]', isOpen);
    lines.bottom?.classList.toggle('-rotate-45', isOpen);
  };

  toggle.addEventListener('click', () => {
    setOpen(toggle.getAttribute('aria-expanded') !== 'true');
  });

  closeButtons.forEach((button) => {
    button.addEventListener('click', () => {
      setOpen(false);
      toggle.focus();
    });
  });

  document.addEventListener('click', (event) => {
    if (toggle.getAttribute('aria-expanded') !== 'true') return;
    if (toggle.contains(event.target) || menu.contains(event.target)) return;

    setOpen(false);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      setOpen(false);
    }
  });
});

document.querySelectorAll('[data-mobile-submenu-toggle]').forEach((toggle) => {
  const submenuId = toggle.getAttribute('aria-controls');
  const submenu = submenuId ? document.getElementById(submenuId) : null;
  const icon = toggle.querySelector('svg');

  if (!submenu) return;

  toggle.addEventListener('click', () => {
    const isOpen = toggle.getAttribute('aria-expanded') !== 'true';

    toggle.setAttribute('aria-expanded', String(isOpen));
    submenu.classList.toggle('hidden', !isOpen);
    icon?.classList.toggle('rotate-180', isOpen);
  });
});

document.querySelectorAll('.article-table-scroll').forEach((scrollArea) => {
  const shell = scrollArea.closest('.article-table-shell');

  if (!shell) return;

  const updateTableOverflow = () => {
    shell.classList.toggle('has-overflow', scrollArea.scrollWidth > scrollArea.clientWidth + 1);
    shell.classList.toggle('is-table-scrolled', scrollArea.scrollLeft > 8);
  };

  updateTableOverflow();
  scrollArea.addEventListener('scroll', updateTableOverflow, { passive: true });

  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(updateTableOverflow);
    observer.observe(scrollArea);
    observer.observe(scrollArea.firstElementChild || scrollArea);
  } else {
    window.addEventListener('resize', updateTableOverflow);
  }
  
  
});


// Reveal Animation — Desktop only (> 1024px)
if (window.matchMedia('(min-width: 1025px)').matches) {
  const sections = document.querySelectorAll('main section');
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          observer.unobserve(entry.target); // Only once
        }
      });
    }, {
      root: null,
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.1
    });

    sections.forEach(section => observer.observe(section));
  } else {
    // Fallback
    sections.forEach(section => {
        section.classList.add('in-view');
    });
  }
}

// Zoho WebToLeads redirects off-site before submit, so GA4 can't catch the click.
// Flag the submit here; the thank-you page reads it and fires the GA4 event itself.
document.addEventListener('submit', (event) => {
  const form = event.target;
  const formName = form.getAttribute('name') || '';
  if (formName.indexOf('WebToLeads') !== 0) return;

  const sourceField = form.querySelector('[name="LEADCF11"]');

  try {
    sessionStorage.setItem('ds_lead_pending', JSON.stringify({
      form_source: sourceField ? sourceField.value : '',
      form_page: location.pathname,
      ts: Date.now(),
    }));
  } catch (err) {
    // sessionStorage unavailable (private mode, etc.) — nothing to fall back to here.
  }
}, true);